<template>
  <div class="product-form">
    <form
      class="product-form__form"
      @submit.prevent="handleAddToCart"
    >
      <div class="form-group">
        <div
          v-show="!hasOnlyDefaultVariant"
          class="form-group__field"
        >
          <label
            class="visually-hidden"
            for="variant-select"
            v-text="$string('product.form.variants')"
          />

          <div
            v-if="showHeader"
            class="product-form__header"
          >
            <div
              class="
                product-form__title
                product-form__title--size
              "
              v-text="optionTitle"
            />

            <template v-if="hasSizeGuide">
              <btn
                type="button"
                class="size-guide-link"
                modifiers="text"
                :label="$string('product.extra.size_guide')"
                :show-icon="false"
                @click="toggleSizeGuide"
              />
            </template>
          </div>

          <select v-if="!isInitialized">
            <option v-text="selectPlaceholderText" />
          </select>

          <custom-select
            v-else-if="product.variants && !hasSizeSwatches"
            name="id"
            :body-click="bodyClick"
            :options="variantOptions"
            :tab-index="hasOnlyDefaultVariant ? '-1' : '0'"
            :placeholder="selectPlaceholderText"
            :notify-bis="notifyBis"
            @update="selectVariant"
            @state-change="handleOptionsState"
          />

          <custom-swatches
            v-else-if="product.variants && hasSizeSwatches"
            name="id"
            :body-click="bodyClick"
            :options="variantOptions"
            :tab-index="hasOnlyDefaultVariant ? '-1' : '0'"
            :notify-bis="notifyBis"
            :current-variant-id="this.currentVariantId !== 0 ?
              this.currentVariantId :
              variantOptions[0].value
            "
            @update="selectVariant"
          />

          <p
            v-show="showSizeError"
            class="product-form__error"
            v-text="$string('product.form.size_error')"
          />

          <p
            v-if="product.modelSizeCaption && showModelSize"
            class="product-form__model-size-caption body-2"
            v-html="product.modelSizeCaption.value"
          />
        </div>

        <div
          v-show="variant.available"
          class="form-group__footer"
        >
          <btn
            class="main-product__add-to-cart"
            block-class="main-product"
            :label="addToCartText"
            modifiers="add-to-cart block center"
            :show-state-icon="state === 'adding'"
            :show-icon="false"
            type="submit"
            :disabled="!variant.available"
          >
            <template #stateIcon>
              <icon-loading />
            </template>
          </btn>

          <div
            v-if="showBorrowBtn"
            ref="borrowBtn"
            class="product-form__borrow-button-wrapper"
          />

          <template v-if="pickapack">
            <!-- eslint-disable vue/no-unused-refs -->
            <btn
              ref="multipleAtcBtn"
              class="main-product__add-to-cart-multiple"
              :label="addMultipleToCartText"
              modifiers="add-to-cart add-to-cart-multiple block center"
              :show-state-icon="state === 'adding-multiple'"
              :show-icon="false"
              :disabled="!canAddMultiple"
              custom-element="a"
              @click="addMultiple"
              @mouseenter="setTooltipState('show', 'multipleAtcBtn')"
              @mouseleave="setTooltipState('hide', 'multipleAtcBtn')"
            >
              <template
                v-if="showMultipleBtnIcon"
                #icon
              >
                <icon-info />
              </template>

              <template #stateIcon>
                <icon-loading />
              </template>
            </btn>
            <tool-tip
              :visible="showTooltip === 'multipleAtcBtn'"
              target="multipleAtcBtn"
              :label="multipleTooltipText"
              size="auto"
            />
          </template>

          <p
            v-if="isPreorder && product.preorderShippingDate"
            class="product-form__preorder-shipping-date body-2"
            v-html="preorderShippingText"
          />
        </div>

        <slot name="terms" />
      </div>
    </form>

    <p
      v-if="error"
      class="main-product__error body-1"
      v-text="error"
    />
  </div>

  <boost-analytics
    :analytics-event="analyticsEvent"
  />
</template>

<script>

/**
 * Vue: Product form (product-form)
 * -----------------------------------------------------------------------------
 * Product form to handle variant selection and adding to cart.
 *
 * @param {Number} bodyClick - Unique timestamp values to detect body clicks.
 * @param {Boolean} liquid.hasOnlyDefaultVariant - Has only default variant.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 * @param {Boolean} pickapack - Boolean if product is tagged with 'pickapack'.
 * @param {Boolean} isInitialized - Boolean to determine if GQL data exists.
 * @param {Boolean} showHeader - If header should be shown, or not.
 * @param {Boolean} showModelSize - If the model size caption should be shown.
 * @param {Boolean} shopTheLook - If form is in Shop the Look modal, or not.
 * @param {Boolean} mainProductForm - If this is the main product form of a PDP.
 *
 */
import { mapActions, mapGetters } from 'vuex'
import timings from '~/config/timings'
import CustomSelect from '~async/custom-select/custom-select'
import CustomSwatches from '~async/custom-swatches/custom-swatches'
import ToolTip from '~async/tool-tip/tool-tip'
import BoostAnalytics from '~global/boost-analytics/boost-analytics'
import Btn from '~global/btn/btn'
import IconInfo from '~icons/misc/info.svg'
import IconLoading from '~icons/misc/loading.svg'

import { moveAsyncElement } from '~/helpers/general'

export default {
  name: 'ProductForm',

  components: {
    BoostAnalytics,
    Btn,
    ToolTip,
    CustomSelect,
    IconLoading,
    IconInfo,
    CustomSwatches,
  },

  props: {
    bodyClick: {
      type: Number,
      default: 0,
    },
    liquid: {
      type: Object,
      default: () => ({
        hasOnlyDefaultVariant: false,
      }),
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    pickapack: {
      type: Boolean,
      default: false,
    },
    notifyBis: {
      type: Boolean,
      default: false,
    },
    giftCard: {
      type: Boolean,
      default: false,
    },
    isInitialized: {
      type: Boolean,
      default: true,
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
    showModelSize: {
      type: Boolean,
      default: true,
    },
    shopTheLook: {
      type: Boolean,
      default: true,
    },
    hasSizeGuide: {
      type: [Boolean, Object],
      default: true,
    },
    hasSizeSwatches: {
      type: Boolean,
      default: false,
    },
    mainProductForm: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'update-variant': (payload) => typeof payload === 'object',
    'select-state-change': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      analyticsEvent: {},
      currentVariantId: this.variant?.id || 0,
      error: '',
      lineItemProperties: {},
      quantity: 1,
      state: 'ready',
      timeout: {},
      buyMultipleThreshold: 3,
      showTooltip: false,
      isSizeSelected: false,
      showSizeError: false,
    }
  },

  computed: {

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      let label
      if (this.isPreorder) {
        label = this.$string('product.cta.preorder')
      } else {
        label = this.$string('product.cta.add_to_cart')
      }

      return this.state === 'added'
        ? this.$string('product.cta.added_to_cart')
        : label
    },

    /**
     * Compute pre-order shipping text.
     * @returns {String}
     */
    preorderShippingText() {
      if (!this.isPreorder || !this.product.preorderShippingDate) {
        return ''
      }

      const metafieldValue = this.product.preorderShippingDate.value
      const dateArray = metafieldValue.split('-')
      dateArray.push(dateArray.shift())
      const formattedDate = dateArray.join('-')

      const monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ]

      const date = new Date(formattedDate)
      const month = monthNames[date.getMonth()]
      const day = date.getDate()
      const year = date.getFullYear()

      let dateString = `${this.$string('product.extra.preorder_shipping')} ${month} ${day}`
      if (year !== new Date().getFullYear()) {
        dateString = dateString.concat(`, ${year}`)
      }

      return dateString
    },

    /**
     * Compute add multiple to cart text.
     * @returns {String}
     */
    addMultipleToCartText() {
      let label = this.$string(
        'product.cta.add_multiple_to_cart',
        { replace: { number: this.buyMultipleThreshold } },
      )

      if (this.variant.inventory < this.buyMultipleThreshold) {
        label += ` - ${this.$string('product.cta.unavailable')}`
      }

      return this.state === 'added-multiple'
        ? this.$string('product.cta.added_to_cart')
        : label
    },

    multipleTooltipText() {
      const text = this.$string(
        'product.cta.unavailable_info',
        { replace: { number: this.buyMultipleThreshold } },
      )

      return text
    },

    /**
     * Compute select placeholder text.
     * @returns {String}
     */
    selectPlaceholderText() {
      let label = this.$string('product.form.select_size')
      if (this.giftCard) {
        label = this.$string('product.form.select_amount')
      }

      return label
    },

    /**
     * Compute has only default variant from Liquid and product objects.
     * @returns {Boolean}
     */
    hasOnlyDefaultVariant() {
      return this.liquid.hasOnlyDefaultVariant || this.product.hasOnlyDefaultVariant
    },

    /**
     * Compute if this is a pre-order product.
     * @returns {Boolean}
     */
    isPreorder() {
      if (!this.product) {
        return false
      }

      const tag = this.product.tags.find((data) => {
        const clean = data.toLowerCase()
        return clean === 'preorder'
      })

      return Boolean(tag)
    },

    /**
     * Compute variant inventory to determine if multiple can be added to cart.
     * @returns {Boolean}
     */
    canAddMultiple() {
      return this.variant.inventory >= this.buyMultipleThreshold
    },

    /**
     * Compute whether to show icon on multiple atc button.
     * @returns {Boolean}
     */
    showMultipleBtnIcon() {
      if (this.variant.inventory >= this.buyMultipleThreshold) {
        return false
      }

      return true
    },

    /**
     * Compute variant options in appropriate format.
     * @returns {Array}
     */
    variantOptions() {
      if (!this.product || !this.product.variants) {
        return []
      }

      const options = this.product.variants.map((variant) => {
        let caption = ''

        if (!variant.available && this.notifyBis) {
          caption = this.$string('product.back_in_stock.notify_me')
        } else if (!variant.available) {
          caption = this.$string('product.form.sold_out')
        } else if (!this.giftCard &&
          variant.inventory < 2 &&
          variant.inventory > 0) {
          caption = this.$string(
            'product.form.remaining',
            { replace: { inventory: variant.inventory } },
          )
        }

        return {
          value: variant.id,
          title: this.getVariantTitle(variant),
          caption,
          disabled: !variant.available,
        }
      })

      return options
    },

    /**
     * Compute which translation to use for option title.
     * @returns {String}
     */
    optionTitle() {
      if (this.giftCard) {
        return this.$string('product.form.amount')
      }

      return this.$string('product.form.size')
    },

    /**
     * Compute whether ot show borrow button
     * @returns {Boolean}
     */
    showBorrowBtn() {
      return this.mainProductForm && !this.shopTheLook && !this.isPreorder
    },
  },

  watch: {

    /**
     * Watch currentVariantId to update CANVAS variant when select changes.
     */
    currentVariantId() {
      if (this.variant.id === this.currentVariantId) {
        return
      }

      const variant = this.getVariantFromId(this.currentVariantId)

      if (!variant) {
        return
      }

      this.$emit('update-variant', variant)
    },

    /**
     * Watch product to update selected variant when product data is loaded.
     */
    product() {
      this.getCurrentVariant()
    },
  },

  mounted() {
    this.getCurrentVariant()

    if (this.showBorrowBtn) {
      moveAsyncElement(
        '[js-product-form="borrow"]',
        this.$refs.borrowBtn,
      )
    }
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Toggle Size Guide overlay
     */
    toggleSizeGuide() {
      if (this.product.sizeGuideId) {
        cnvs.EventBus.emit('size-guide:toggle', {
          sizeGuide: this.product.sizeGuideId,
        })

        this.toggleOverlay({
          ignoreDismissed: true,
          namespace: 'sizeGuide',
        })
      }
    },

    /**
     * Get current variant on page load or product update.
     */
    getCurrentVariant() {
      if (
        this.variant.id === this.currentVariantId ||
        !this.product ||
        !this.product.variants.length
      ) {
        return
      }

      if (this.hasOnlyDefaultVariant) {
        this.currentVariantId = this.product.variants[0].id
        return
      }

      /**
       * Pick the first in stock variant.
       * - If no variants are in stock use first.
       * - Or use variant from URL if set.
       */
      const inStockVariants = this.product.variants.filter((variant) => {
        return variant.available
      })

      let defaultVariant

      if (this.variant) {
        defaultVariant = this.variant
      } else {
        defaultVariant = inStockVariants.length
          ? inStockVariants[0]
          : this.product.variants[0]
      }

      const variant = this.getVariantFromUrl() || defaultVariant

      this.currentVariantId = variant.id
    },

    /**
     * Get initial variant from URL parameter.
     * @returns {Object}
     */
    getVariantFromUrl() {
      const params = new URLSearchParams(document.location.search.substring(1))
      const variantId = Number(params.get('variant'))
      return this.getVariantFromId(variantId)
    },

    /**
     * Get variant object from variant ID.
     * @returns {Object}
     */
    getVariantFromId(variantId) {
      return this.product.variants.find((variant) => {
        return variant.id === variantId
      })
    },

    /**
     * Get variant title.
     * @param {Object} variant - Variant object.
     * @returns {String}
     */
    getVariantTitle(variant) {
      const data = variant.title.split(' / ')
      const title = data[data.length - 1].trim()
      return title
    },

    /**
     * Handle quantity selector update.
     * @param {String} quantity - New quantity.
     */
    handleQuantityUpdate(quantity) {
      this.quantity = quantity
    },

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart(event, customStates = false) {
      if (this.product.variants && !this.isSizeSelected) {
        this.showSizeError = true
        return
      }

      if (!this.variant) {
        return
      }

      clearTimeout(this.timeout)
      this.error = ''
      let states = [
        'adding',
        'added',
      ]

      if (customStates) {
        states = customStates
      }

      this.state = states[0]

      if (cnvs.storybook) {
        this.timeout = setTimeout(() => {
          this.state = 'added'
        }, timings.xl)

        this.timeout = setTimeout(() => {
          this.state = 'ready'
        }, timings.xxl)

        return
      }

      const items = [
        {
          id: this.variant.id,
          properties: this.lineItemProperties,
          quantity: this.quantity,
        },
      ]

      try {
        await this.addItem(items)

        this.analyticsEvent = {
          pid: this.product.id.toString(),
          u: 'add_to_cart',
        }

        if (!this.shopTheLook) {
          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          })
        }

        this.state = states[1]

        this.timeout = setTimeout(() => {
          this.state = 'ready'
        }, timings.xxl)

      } catch (error) {
        this.state = 'error'

        if (error.length) {
          this.error = error
          return
        }

        reportError('Failed to add product to cart', error)
      }
    },

    /**
     * Add multiple items to cart
     */
    addMultiple() {
      if (!this.canAddMultiple) {
        return
      }

      this.quantity = this.buyMultipleThreshold
      this.handleAddToCart('click', ['adding-multiple', 'added-multiple'])
      this.quantity = 1
    },

    /**
     * Calculate tooltip state
     * @param {String} state - Show / Hide
     * @param {String} target - :ref target
     */
    setTooltipState(state, target) {

      if (this.canAddMultiple || state === 'hide') {
        this.showTooltip = false
        return
      }

      this.showTooltip = target
    },

    /**
     * Set Current variant ID.
     * @param {String} variant
     */
    selectVariant(variant) {
      this.currentVariantId = variant
      this.isSizeSelected = true
      this.showSizeError = false
    },

    /**
     * Emit data about custom select when its state changes.
     * @param {Object} data
     */
    handleOptionsState(data) {
      this.$emit('select-state-change', data)
    },
  },
}
</script>
